import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { getDailyPhrase } from "@/lib/dailyPhrases";
import {
  Plus, ClipboardList, FileSpreadsheet, Mic, ArrowRight, Clock,
  Zap, Sun, Truck, MessageSquare,
} from "lucide-react";
import PushToggle from "@/components/PushToggle";
import InstallPWA from "@/components/InstallPWA";
import UpcomingOrdersWidget from "@/components/UpcomingOrdersWidget";
import PjcTodayCard from "@/components/PjcTodayCard";

const TIPO_LABELS = {
  AVERIA: "Avería",
  SERVICIO_PUNTUAL: "Serv. Puntual",
  OBRA: "Obra",
  MANTENIMIENTO: "Mant.",
  VARIOS: "Varios",
};

const TIPO_COLORS = {
  AVERIA: "bg-red-100 text-red-700 border-red-200",
  SERVICIO_PUNTUAL: "bg-purple-100 text-purple-700 border-purple-200",
  OBRA: "bg-orange-100 text-orange-700 border-orange-200",
  MANTENIMIENTO: "bg-emerald-100 text-emerald-700 border-emerald-200",
  VARIOS: "bg-slate-100 text-slate-700 border-slate-200",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [daily, setDaily] = useState(null);
  const [recent, setRecent] = useState([]);
  const [openShift, setOpenShift] = useState(null);
  const [lastShiftToday, setLastShiftToday] = useState(null);
  const [viWeekly, setViWeekly] = useState(undefined); // undefined=loading, null=none, obj=done
  const [fleetSummary, setFleetSummary] = useState(null);
  const isAdmin = user?.role === "admin" || user?.role === "sadmin";

  const phrase = useMemo(() => getDailyPhrase(), []);

  useEffect(() => {
    api.get("/stats/daily").then((r) => setDaily(r.data)).catch(() => {});
    api.get("/work-logs", { params: { limit: 5 } }).then((r) => setRecent(r.data)).catch(() => {});
    api.get("/vehicle-inspections/mine-this-week").then((r) => setViWeekly(r.data)).catch(() => setViWeekly(null));
    if (isAdmin) {
      api.get("/admin/vehicle-inspections/weekly-summary")
        .then((r) => setFleetSummary(r.data))
        .catch(() => {});
    }
    if (user?.can_clock_in) {
      api.get("/timeclock/current").then((r) => setOpenShift(r.data)).catch(() => {});
      api.get("/timeclock/mine", { params: { limit: 1 } }).then((r) => {
        const last = Array.isArray(r.data) ? r.data[0] : null;
        if (!last?.entry_at) { setLastShiftToday(null); return; }
        const d = new Date(last.entry_at);
        const today = new Date();
        const sameDay = d.getFullYear() === today.getFullYear()
          && d.getMonth() === today.getMonth()
          && d.getDate() === today.getDate();
        setLastShiftToday(sameDay ? last : null);
      }).catch(() => {});
    }
  }, [user?.can_clock_in, isAdmin]);

  const shiftEntryHM = useMemo(() => {
    if (!openShift?.entry_at) return null;
    try {
      const d = new Date(openShift.entry_at);
      return d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
    } catch { return null; }
  }, [openShift]);

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* HERO */}
      <section
        className="relative overflow-hidden rounded-3xl p-6 text-white"
        style={{
          backgroundImage:
            "linear-gradient(135deg, rgba(11,42,74,0.78) 0%, rgba(14,120,194,0.62) 100%), url(https://images.pexels.com/photos/356036/pexels-photo-356036.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=500&w=1000)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="relative z-10">
          <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] font-semibold text-[#FFC740]">
            <Sun size={14} /> Hola, {user?.name?.split(" ")[0]}
          </span>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight mt-2 leading-tight">
            Tu jornada,<br />
            <span className="bg-gradient-to-r from-[#FFC740] to-[#FF8A1F] bg-clip-text text-transparent">
              en un toque.
            </span>
          </h1>
          <p className="text-white/85 text-sm mt-3 max-w-md">
            Dicta tu parte por voz o rellénalo en segundos. Y ficha tu jornada en cualquier lugar.
          </p>

          {/* Daily motivational quote — different phrase every working day */}
          {phrase && (
            <div className="mt-5 pt-5 border-t border-white/15 max-w-xl">
              <p className="font-display font-extrabold text-2xl sm:text-3xl leading-tight tracking-tight text-white">
                {phrase}
              </p>
            </div>
          )}
        </div>
        <div
          aria-hidden
          className="absolute -bottom-20 -right-16 w-72 h-72 rounded-full opacity-30 pointer-events-none"
          style={{ background: "radial-gradient(closest-side, #FFC740, transparent 70%)" }}
        />
      </section>

      {/* CTA grid */}
      <section className="grid grid-cols-2 gap-4">
        <Link
          to="/new"
          data-testid="cta-new-log"
          className="relative overflow-hidden rounded-2xl p-5 text-white gradient-sun
                     shadow-[0_14px_30px_-12px_rgba(245,130,32,0.55)]
                     transition-all hover:-translate-y-[2px]"
        >
          <Plus size={28} strokeWidth={2.5} />
          <div className="font-display font-extrabold text-xl mt-3 leading-tight">
            Nuevo<br />Parte
          </div>
          <div className="text-xs uppercase tracking-wider mt-2 opacity-90">Voz · Formulario</div>
        </Link>
        {user?.can_clock_in ? (
          <Link
            to="/fichar"
            data-testid="cta-fichar"
            className={`relative overflow-hidden rounded-2xl p-5 text-white
                       shadow-[0_14px_30px_-12px_rgba(11,42,74,0.55)]
                       transition-all hover:-translate-y-[2px]
                       ${openShift
                         ? "bg-gradient-to-br from-emerald-600 to-emerald-800"
                         : "bg-gradient-to-br from-[#0E78C2] to-[#0B2A4A]"}`}
          >
            <Clock size={28} strokeWidth={2.5} />
            <div className="font-display font-extrabold text-xl mt-3 leading-tight">
              Fichar<br />Jornada
            </div>
            {openShift ? (
              <div
                data-testid="fichar-status-open"
                className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white/20 backdrop-blur-sm px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider"
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FFC740] opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FFC740]" />
                </span>
                Fichado · {shiftEntryHM || "hoy"}
              </div>
            ) : lastShiftToday ? (
              <div
                data-testid="fichar-status-closed-today"
                className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-white/90"
              >
                Jornada cerrada hoy
              </div>
            ) : (
              <div
                data-testid="fichar-status-closed"
                className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-white/90"
              >
                Sin fichar hoy
              </div>
            )}
          </Link>
        ) : (
          <Link
            to="/logs"
            data-testid="cta-logs"
            className="relative overflow-hidden rounded-2xl p-5 bg-white border border-slate-200
                       shadow-[0_14px_30px_-18px_rgba(11,42,74,0.35)]
                       transition-all hover:-translate-y-[2px]"
          >
            <ClipboardList size={28} strokeWidth={2.2} className="text-[#0E78C2]" />
            <div className="font-display font-extrabold text-xl mt-3 leading-tight text-[#0B2A4A]">
              Mis<br />Partes
            </div>
            <div className="text-xs uppercase tracking-wider mt-2 text-slate-500">Historial completo</div>
          </Link>
        )}
      </section>

      {/* Mis Partes — full-width card (shown for users with fichaje, since the CTA slot is taken) */}
      {user?.can_clock_in && (
        <Link
          to="/logs"
          data-testid="cta-logs"
          className="brutal-card p-4 flex items-center gap-4 transition-all hover:-translate-y-[1px] hover:shadow-lg"
        >
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#E6F2FB] to-[#C7E0F1] border border-[#9CC8E6] flex items-center justify-center">
            <ClipboardList size={22} strokeWidth={2.2} className="text-[#0E78C2]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-extrabold text-lg text-[#0B2A4A] leading-tight">Mis Partes</div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mt-0.5">Historial completo</div>
          </div>
          <ArrowRight size={18} className="text-slate-400" />
        </Link>
      )}

      {/* Push notifications toggle — only for users with fichaje */}
      {user?.can_clock_in && <PushToggle />}

      {/* Weekly vehicle inspection — status-aware badge */}
      {(() => {
        // undefined = loading, null = not done yet, object = done this week
        const s = viWeekly;
        const config =
          s === undefined ? { label: "Cargando…", cls: "bg-slate-100 text-slate-500" }
          : s === null ? { label: "Pendiente esta semana", cls: "bg-rose-100 text-rose-800 animate-pulse" }
          : s.status === "todo_correcto" ? { label: "Ya revisado", cls: "bg-emerald-100 text-emerald-800" }
          : s.status === "incidencia" ? { label: "Incidencia enviada", cls: "bg-amber-100 text-amber-800" }
          : { label: "No usar", cls: "bg-rose-200 text-rose-900" };
        return (
          <Link
            to="/vehicle-inspection/new"
            data-testid="cta-vehicle-inspection"
            className="brutal-card p-4 flex items-center gap-4 transition-all hover:-translate-y-[1px] hover:shadow-lg bg-gradient-to-br from-[#FFF6E6] to-[#FFE4BD] border-[#FFD893]"
          >
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#FF8A1F] to-[#F5A623] flex items-center justify-center shadow-md">
              <Truck size={22} strokeWidth={2.2} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-extrabold text-lg text-[#0B2A4A] leading-tight">
                Revisión semanal de vehículo
              </div>
              <div className="text-xs uppercase tracking-wider text-slate-600 mt-0.5">
                {s?.matricula ? `${s.matricula} · ` : ""}menos de 1 min si todo OK
              </div>
            </div>
            <span
              data-testid="vi-week-badge"
              className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full whitespace-nowrap ${config.cls}`}
            >
              {config.label}
            </span>
          </Link>
        );
      })()}

      {/* Suggestions inbox — anonymous */}
      <Link
        to="/sugerencias"
        data-testid="cta-sugerencias"
        className="brutal-card p-4 flex items-center gap-4 transition-all hover:-translate-y-[1px] hover:shadow-lg bg-white"
      >
        <div className="w-11 h-11 rounded-xl bg-slate-100 text-[#0E78C2] flex items-center justify-center">
          <MessageSquare size={22} strokeWidth={2.2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-extrabold text-lg text-[#0B2A4A] leading-tight">
            Sugerencias
          </div>
          <div className="text-xs uppercase tracking-wider text-slate-500 mt-0.5">
            Buzón anónimo · va al equipo Suntelco
          </div>
        </div>
        <ArrowRight size={18} className="text-slate-500" />
      </Link>

      {/* Fleet overview widget — admin/sadmin only */}
      {isAdmin && fleetSummary && (
        <Link
          to="/vehicle-inspections"
          data-testid="fleet-summary-card"
          className="brutal-card p-4 space-y-3 transition-all hover:-translate-y-[1px] hover:shadow-lg bg-white block"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg bg-slate-900 text-white flex items-center justify-center">
                <Truck size={18} />
              </div>
              <div>
                <div className="label-up text-slate-500">Flota · esta semana</div>
                <div className="font-display font-black text-lg text-[#0B2A4A]">
                  {fleetSummary.done}/{fleetSummary.active_total} revisiones
                </div>
              </div>
            </div>
            <ArrowRight size={18} className="text-slate-400" />
          </div>
          {/* Progress bar */}
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all"
              style={{
                width: `${
                  fleetSummary.active_total
                    ? Math.round((fleetSummary.done / fleetSummary.active_total) * 100)
                    : 0
                }%`,
              }}
            />
          </div>
          {/* Compact status counters */}
          <div className="grid grid-cols-4 gap-2 text-center">
            <div className="rounded-lg bg-emerald-50 text-emerald-800 py-1.5">
              <div className="font-black text-lg leading-none">{fleetSummary.counts.todo_correcto}</div>
              <div className="text-[9px] uppercase tracking-wide mt-0.5">Todo OK</div>
            </div>
            <div className="rounded-lg bg-amber-50 text-amber-800 py-1.5">
              <div className="font-black text-lg leading-none">{fleetSummary.counts.incidencia}</div>
              <div className="text-[9px] uppercase tracking-wide mt-0.5">Incidencias</div>
            </div>
            <div className="rounded-lg bg-rose-50 text-rose-800 py-1.5">
              <div className="font-black text-lg leading-none">{fleetSummary.counts.no_circular}</div>
              <div className="text-[9px] uppercase tracking-wide mt-0.5">No usar</div>
            </div>
            <div className="rounded-lg bg-slate-50 text-slate-600 py-1.5">
              <div className="font-black text-lg leading-none">{fleetSummary.pending}</div>
              <div className="text-[9px] uppercase tracking-wide mt-0.5">Pendiente</div>
            </div>
          </div>
          {/* Incidents list (max 4) */}
          {fleetSummary.incidents.length > 0 && (
            <div className="pt-1 border-t border-slate-100 space-y-1">
              <div className="label-up text-rose-700">Incidencias abiertas</div>
              <ul className="text-xs space-y-1">
                {fleetSummary.incidents.slice(0, 4).map((i) => (
                  <li key={i.user_id} className="flex items-center gap-1.5">
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${
                        i.status === "no_circular" ? "bg-rose-600" : "bg-amber-500"
                      }`}
                    />
                    <span className="font-semibold">{i.matricula}</span>
                    <span className="text-slate-500 truncate">— {i.user_name}</span>
                  </li>
                ))}
                {fleetSummary.incidents.length > 4 && (
                  <li className="text-slate-400 italic">
                    +{fleetSummary.incidents.length - 4} más…
                  </li>
                )}
              </ul>
            </div>
          )}
        </Link>
      )}

      {/* PWA install prompt (shows only when browser flagged as installable) */}
      <InstallPWA />

      {/* Personal daily Pre-Job Check */}
      <PjcTodayCard />

      {/* Mi agenda: próximas órdenes asignadas */}
      <UpcomingOrdersWidget />

      {/* STATS */}
      <section className="brutal-card p-5">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-extrabold text-xl text-[#0B2A4A]" data-testid="resumen-diario-title">Resumen diario</h2>
          <span className="brutal-chip" data-testid="resumen-diario-date">
            {daily?.date
              ? new Date(daily.date + "T00:00:00").toLocaleDateString("es-ES", { day: "2-digit", month: "short" })
              : "Hoy"}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="rounded-xl bg-gradient-to-br from-[#E6F2FB] to-[#C7E0F1] border border-[#9CC8E6] p-4" data-testid="stat-horas-fichadas">
            <div className="flex items-center justify-between">
              <Clock size={18} className="text-[#0E78C2]" />
              <span className="text-[9px] uppercase font-semibold tracking-wider text-[#0B2A4A]/60">
                {daily?.entradas_hoy ? `${daily.entradas_hoy} ${daily.entradas_hoy === 1 ? "sesión" : "sesiones"}` : ""}
              </span>
            </div>
            <div className="text-3xl font-display font-extrabold leading-none mt-2 text-[#0B2A4A]">
              {daily ? Number(daily.horas_fichadas || 0).toFixed(1) : "—"}
              <span className="text-base font-semibold text-[#0B2A4A]/60 ml-1">h</span>
            </div>
            <div className="text-[10px] uppercase font-semibold tracking-wider mt-1 text-[#0B2A4A]/70">Fichadas hoy</div>
          </div>
          <div className="rounded-xl bg-gradient-to-br from-[#FFF6E6] to-[#FFE4BD] border border-[#FFD893] p-4" data-testid="stat-horas-parte">
            <div className="flex items-center justify-between">
              <FileSpreadsheet size={18} className="text-[#B96B00]" />
              <span className="text-[9px] uppercase font-semibold tracking-wider text-[#7A4500]/70">
                {daily?.partes_hoy ? `${daily.partes_hoy} parte${daily.partes_hoy === 1 ? "" : "s"}` : ""}
              </span>
            </div>
            <div className="text-3xl font-display font-extrabold leading-none mt-2 text-[#7A4500]">
              {daily ? Number(daily.horas_partes || 0).toFixed(1) : "—"}
              <span className="text-base font-semibold text-[#7A4500]/70 ml-1">h</span>
            </div>
            <div className="text-[10px] uppercase font-semibold tracking-wider mt-1 text-[#7A4500]/80">Horas de parte hoy</div>
          </div>
        </div>
      </section>

      {/* RECENT */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-extrabold text-xl text-[#0B2A4A]">Partes recientes</h2>
          <Link to="/logs" className="text-xs font-semibold uppercase tracking-wider text-[#0E78C2] hover:underline">
            Ver todos →
          </Link>
        </div>
        {recent.length === 0 ? (
          <div className="brutal-card p-6 text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-orange-50 flex items-center justify-center">
              <Mic size={22} className="text-[#F5A623]" />
            </div>
            <div className="font-semibold mt-3 text-[#0B2A4A]">Aún no hay partes registrados</div>
            <p className="text-sm text-slate-500 mt-1">Crea tu primer parte ahora.</p>
            <Link
              to="/new"
              className="brutal-btn-primary inline-flex items-center justify-center mt-4 gap-2"
            >
              Crear parte <ArrowRight size={16} />
            </Link>
          </div>
        ) : (
          <ul className="space-y-3">
            {recent.map((r) => (
              <li key={r.id} data-testid={`recent-${r.id}`}>
                <Link
                  to={`/logs/${r.id}`}
                  className="brutal-card p-4 flex items-center gap-4 transition-all hover:-translate-y-[1px] hover:shadow-lg"
                >
                  <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider border ${TIPO_COLORS[r.tipo_servicio] || "bg-slate-100"}`}>
                    {TIPO_LABELS[r.tipo_servicio] || r.tipo_servicio}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate text-[#0B2A4A]">{r.nombre_servicio || "—"}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                      <Clock size={12} /> {r.fecha} · {r.created_by_name}
                    </div>
                  </div>
                  <ArrowRight size={16} className="text-slate-400" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ADMIN CTA */}
      {user?.role === "admin" && (
        <section className="rounded-2xl p-5 text-white gradient-sky relative overflow-hidden">
          <div className="relative z-10 flex items-center justify-between gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#FFC740]">
                Solo administradores
              </div>
              <h3 className="font-display font-extrabold text-xl mt-1">Exportar para Holded</h3>
              <p className="text-sm text-white/80 mt-1">CSV listo para importar a tu ERP.</p>
            </div>
            <Link
              to="/export"
              data-testid="cta-export"
              className="bg-white text-[#0B2A4A] font-semibold rounded-xl h-11 px-4 inline-flex items-center gap-2 hover:bg-[#FFC740] transition-colors"
            >
              <FileSpreadsheet size={16} /> Exportar
            </Link>
          </div>
          <Zap
            aria-hidden
            size={180}
            className="absolute -right-6 -bottom-10 text-white/10"
            strokeWidth={1}
          />
        </section>
      )}
    </div>
  );
}
